// =============================================================================
// FOOTER COMPONENT
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Heart, ChevronUp, ChevronDown } from 'lucide-react';
import { NewsletterSignup } from '@/components/Newsletter/NewsletterSignup';
import type { NavItem } from '@/types';

interface FooterProps {
  brand?: string;
  description?: string;
  links?: NavItem[];
  socialLinks?: NavItem[];
  copyright?: string;
  showNewsletter?: boolean;
  privacyLink?: string;
  termsLink?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export const Footer: React.FC<FooterProps> = ({
  brand = 'Awani',
  links = [],
  socialLinks = [],
  copyright = `© ${new Date().getFullYear()} Awani. All rights reserved.`,
  showNewsletter = true,
  collapsible = false,
  defaultCollapsed = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const footerRef = useRef<HTMLElement>(null);

  const toggleFooter = () => {
    setIsCollapsed(prev => !prev);
  };

  // Listen for custom event from storybook keyboard navigation
  useEffect(() => {
    if (!collapsible) return;
    
    const handleToggleFooter = (e: Event) => {
      e.preventDefault();
      setIsCollapsed(prev => !prev);
    };
    
    // Listen on both window and document to ensure we catch it
    window.addEventListener('toggleFooter', handleToggleFooter);
    document.addEventListener('toggleFooter', handleToggleFooter);
    
    return () => {
      window.removeEventListener('toggleFooter', handleToggleFooter);
      document.removeEventListener('toggleFooter', handleToggleFooter);
    };
  }, [collapsible]);

  // Add class to body when footer is open to adjust layout
  useEffect(() => {
    if (!collapsible) return;
    
    if (!isCollapsed) {
      document.body.classList.add('footer-open');
    } else {
      document.body.classList.remove('footer-open');
    }
    
    return () => {
      document.body.classList.remove('footer-open');
    };
  }, [collapsible, isCollapsed]);

  return (
    <>
      {collapsible && (
        <button
          className="footer-toggle-button"
          onClick={toggleFooter}
          aria-label={isCollapsed ? 'Show footer' : 'Hide footer'}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? (
            <>
              <ChevronUp size={20} />
              <span>Show Footer</span>
            </>
          ) : (
            <>
              <ChevronDown size={20} />
              <span>Hide Footer</span>
            </>
          )}
        </button>
      )}
      <footer 
        ref={footerRef}
        className={`footer ${collapsible ? 'collapsible' : ''} ${isCollapsed ? 'collapsed' : ''}`}
      >
        <div className="container">
          <div className="footer-content">
            {/* Brand Section - Left Side */}
            <div className="footer-brand">
              <img 
                src="/images/logo.png" 
                alt={`${brand} logo`}
                className="footer-logo"
                width="48"
                height="48"
              />
              <h3 className="brand-text">{brand}</h3>
            </div>

            {/* Links and Social Section - Right Side */}
            <div className="footer-right-group">
              {/* Links Column */}
              <div className="footer-links">
                <h4>Links</h4>
                {links.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                  >
                    {link.label}
                  </a>
                ))}
              </div>

              {/* Newsletter Section - Full Width Below */}
              {showNewsletter && (
                <div className="footer-newsletter-section">
                  <NewsletterSignup
                    variant="footer"
                    title="Stay Updated"
                    description="Get the latest insights and updates delivered to your inbox."
                    placeholder="Enter your email"
                    buttonText="Subscribe"
                    showIcon={false}
                  />
                </div>
              )}

              {/* Social Column */}
              {socialLinks.length > 0 && (
                <div className="footer-social">
                  <h4>Connect</h4>
                  {socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.label}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="footer-bottom">
            <p>
              Made with &nbsp;<Heart size={16} style={{ color: 'var(--color-error-700)' }} />&nbsp; by{' '}
              <a href="https://alexfinger.com" target="_blank" rel="noopener noreferrer">
               Alex Finger Consulting
              </a> | {copyright}
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};
